<script>
import { fade, fly } from "svelte/transition";
import { cubicOut } from "svelte/easing";
import { onMount } from "svelte";
import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import { toast } from '@zerodevx/svelte-toast';
import { authState, updateTeam, updateUser } from '@app/store/authStore.svelte.js';
import { entitlements } from '@app/lib/entitlements.js';
import * as m from '@app/paraglide/messages.js';
import { BarLoader } from 'svelte-loading-spinners';
import { auth, functions } from '@app/configs/firebase.js';
import { httpsCallable } from 'firebase/functions';
import { v4 as uuidv4 } from 'uuid';
import MembersTab from './team/MembersTab.svelte';
import GroupsTab from './team/GroupsTab.svelte';
import { loadTeam, normalizeRole, errorMessage, motion, updateTeamSeats } from './team/teamApi.js';
import Swal from 'sweetalert2';
import { SWAL_BASE, SWAL_CLASSES } from '@app/lib/swalClasses.js';

const email = auth.currentUser.email,
    uid = auth.currentUser.uid;

let users = $state([]),
    groups = $state([]),
    activeTab = $state('members'),
    loading = $state(false),
    creating = $state(false);

/* The role is the only admin signal; the legacy `admin` boolean is no longer read. */
const isAdmin = $derived(normalizeRole(authState.user?.role) === 'admin' && !authState.user?.disable);

/* Pricing v2 seat limit; null on legacy plans (unlimited). */
const seatLimit = $derived(entitlements()?.seats?.limit ?? null);
const seatPercent = $derived(seatLimit ? Math.min(100, Math.round((users.length / seatLimit) * 100)) : 0);
const seatsAvailable = $derived(seatLimit ? Math.max(0, seatLimit - users.length) : 0);
/* Only the billing owner (the team owner) can buy extra users; other admins are told to ask them. */
const isBillingOwner = $derived((authState.team?.owner || "").toLowerCase() === (email || "").toLowerCase());

/** Pro v2: 3 users included, extra users bought here and added to the Pro subscription. */
async function addUsers() {
    const ent = entitlements();
    const included = ent?.seats?.included ?? 3;
    const currentExtra = ent?.seats?.extra ?? 0;
    const yearly = ent?.interval === "year";
    const minimum = Math.max(0, users.length - included);
    const result = await Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.confirmCancel,
        title: m.team_manager_add_users_title(),
        text: m.team_manager_add_users_text({ included, price: yearly ? m.team_manager_add_users_price_yearly() : m.team_manager_add_users_price_monthly() }),
        input: "number",
        inputLabel: m.team_manager_add_users_label(),
        inputValue: String(Math.max(currentExtra, minimum) + (seatsAvailable === 0 ? 1 : 0)),
        inputAttributes: { min: String(minimum), max: "50", step: "1" },
        showCancelButton: true,
        confirmButtonText: m.team_manager_add_users_confirm(),
        cancelButtonText: m.cancel(),
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),
        preConfirm: async (value) => {
            const extraSeats = Number(value);
            if (!Number.isInteger(extraSeats) || extraSeats < minimum || extraSeats > 50) {
                Swal.showValidationMessage(m.team_manager_add_users_invalid({ min: minimum }));
                return false;
            }
            try {
                return await updateTeamSeats(extraSeats);
            } catch (err) {
                Swal.showValidationMessage(errorMessage(err));
                return false;
            }
        },
    });
    if (!result.isConfirmed || !result.value?.data?.seats) return;
    // Apply the new limit now (Add member, CSV import and the bar all read it).
    authState.entitlements = { ...authState.entitlements, seats: result.value.data.seats };
    toast.push(m.team_manager_add_users_success({ limit: result.value.data.seats.limit }), { classes: ['toast-success'] });
}

async function reload() {
    if (!authState.team?.id) return;
    loading = true;
    try {
        const team = await loadTeam(authState.team.id);
        users = team.users;
        groups = team.groups;
        updateTeam({ ...authState.team, members: team.users.map((u) => u.email) });
    } catch (err) {
        console.error('Error loading team', err);
        toast.push(m.team_manager_load_users_failed({ error: errorMessage(err) }), { classes: ['toast-danger'] });
    } finally {
        loading = false;
    }
}

async function createTeam() {
    creating = true;
    try {
        const res = await httpsCallable(functions, 'v8CreateTeam')({
            id: uuidv4(),
            owner: email,
            ownerId: uid,
            members: [email],
        });
        updateTeam(res.data.team);
        updateUser(res.data.users[0]);
        toast.push(m.team_manager_team_created_success(), { classes: ['toast-success'] });
        await reload();
    } catch (err) {
        console.error(err);
        toast.push(m.team_manager_create_team_failed({ error: errorMessage(err) }), { classes: ['toast-danger'] });
    } finally {
        creating = false;
    }
}

onMount(() => {
    if (authState.team && isAdmin) reload();
});
</script>
<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'teamManager' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="teamManagerTitle"
    aria-modal="true"
    tabindex="-1"
    id="TeamManagerModal">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header align-items-center">
                <div class="d-flex align-items-center gap-3 flex-grow-1 min-w-0">
                    <span class="header-icon" aria-hidden="true"><i class="bi bi-people-fill"></i></span>
                    <div class="min-w-0">
                        <h4 class="modal-title mb-0" id="teamManagerTitle">{m.team_manager_title()}</h4>
                        {#if authState.team && isAdmin}
                            <div class="small text-body-secondary text-truncate">{m.team_manager_subtitle({ owner: authState.team.owner })}</div>
                        {/if}
                    </div>
                    {#if authState.team && isAdmin}
                        <div class="seats ms-auto me-3" title={seatLimit ? m.team_manager_seats_used({ used: users.length, limit: seatLimit }) : ''}>
                            {#if seatLimit}
                                <div class="d-flex justify-content-between small mb-1">
                                    <span class="text-body-secondary">{m.team_manager_seats_label()}</span>
                                    <span class="fw-semibold">{users.length} / {seatLimit}</span>
                                </div>
                                <div class="progress" role="progressbar" aria-label={m.team_manager_seats_used({ used: users.length, limit: seatLimit })} aria-valuenow={users.length} aria-valuemin="0" aria-valuemax={seatLimit} style="height: 0.375rem;">
                                    <div class="progress-bar {seatPercent >= 100 ? 'bg-warning' : ''}" style="width: {seatPercent}%"></div>
                                </div>
                                <div class="d-flex justify-content-between align-items-center small mt-1 gap-2">
                                    <span class="text-body-secondary">{m.team_manager_seats_available({ count: seatsAvailable })}</span>
                                    {#if isBillingOwner}
                                        <button type="button" class="btn btn-link btn-sm p-0" onclick={addUsers}>{m.team_manager_add_users_button()}</button>
                                    {/if}
                                </div>
                            {:else}
                                <span class="small text-body-secondary">{m.team_manager_user_count({ count: users.length })}</span>
                            {/if}
                        </div>
                    {/if}
                </div>
                <button type="button" class="btn-close ms-2" aria-label={m.close()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                {#if !authState.team}
                    <div class="team-empty team-empty--hero">
                        <i class="bi bi-people" aria-hidden="true"></i>
                        <p class="fw-semibold mb-1">{m.team_manager_create_team_title()}</p>
                        <p class="small text-body-secondary mb-3">{m.team_manager_create_team_prompt()}</p>
                        <button type="button" class="btn btn-primary" onclick={createTeam} disabled={creating}>
                            {#if creating}<span class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>{/if}
                            {m.team_manager_create_team_button()}
                        </button>
                    </div>
                {:else if isAdmin}
                    <ul class="nav nav-underline mb-3" role="tablist">
                        <li class="nav-item" role="presentation">
                            <button type="button" role="tab" class="nav-link {activeTab === 'members' ? 'active' : ''}" aria-selected={activeTab === 'members'} onclick={() => (activeTab = 'members')}>
                                <i class="bi bi-person me-1"></i>{m.team_manager_members_tab()}
                                <span class="badge rounded-pill soft-badge ms-1">{users.length}</span>
                            </button>
                        </li>
                        <li class="nav-item" role="presentation">
                            <button type="button" role="tab" class="nav-link {activeTab === 'groups' ? 'active' : ''}" aria-selected={activeTab === 'groups'} onclick={() => (activeTab = 'groups')}>
                                <i class="bi bi-diagram-3 me-1"></i>{m.team_manager_groups_tab()}
                                <span class="badge rounded-pill soft-badge ms-1">{groups.length}</span>
                            </button>
                        </li>
                    </ul>

                    {#if loading && users.length === 0}
                        <div class="d-flex justify-content-center align-items-center p-5">
                            <BarLoader size="200" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
                        </div>
                    {:else if activeTab === 'members'}
                        <div in:fly={{ x: -16, duration: motion(200), easing: cubicOut }}>
                            <MembersTab
                                teamId={authState.team.id}
                                ownerEmail={authState.team.owner}
                                selfEmail={email}
                                {users}
                                {groups}
                                {seatLimit}
                                onaddusers={isBillingOwner ? addUsers : null}
                                onchange={reload} />
                        </div>
                    {:else}
                        <div in:fly={{ x: 16, duration: motion(200), easing: cubicOut }}>
                            <GroupsTab teamId={authState.team.id} {users} {groups} onchange={reload} />
                        </div>
                    {/if}
                {:else}
                    <div class="team-empty team-empty--hero">
                        <i class="bi bi-shield-lock" aria-hidden="true"></i>
                        <p class="fw-semibold mb-1">{m.team_manager_no_permission_title()}</p>
                        <p class="small text-body-secondary mb-3">{m.team_manager_no_permission_text()}</p>
                        <button type="button" class="btn btn-outline-secondary" onclick={hideModal}>{m.close()}</button>
                    </div>
                {/if}
            </div>
        </div>
    </div>
</div>

<style>
    .min-w-0 {
        min-width: 0;
    }

    .header-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.5rem;
        height: 2.5rem;
        border-radius: 0.5rem;
        background: var(--bs-primary-bg-subtle);
        color: var(--bs-primary);
        font-size: 1.25rem;
        flex-shrink: 0;
    }

    .seats {
        width: 11rem;
        flex-shrink: 0;
    }

    #TeamManagerModal .modal-body {
        min-height: 60vh;
    }

    /* Shared by the Members, Groups and CSV import views. */
    #TeamManagerModal :global(.team-toolbar) {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.75rem;
    }

    /* Flex, not grid: a panel that is sliding out keeps its space until the transition ends. */
    #TeamManagerModal :global(.team-layout) {
        display: flex;
        gap: 1rem;
        align-items: flex-start;
    }

    #TeamManagerModal :global(.team-layout > .team-table-wrap) {
        flex: 1 1 auto;
        min-width: 0;
    }

    #TeamManagerModal :global(.panel-slot) {
        flex: 0 0 22rem;
        min-width: 0;
    }

    /* Narrow windows: the panel stays on the right as a drawer over the table instead of dropping below it. */
    @media (max-width: 991.98px) {
        #TeamManagerModal :global(.team-layout) {
            position: relative;
        }

        #TeamManagerModal :global(.team-layout:has(> .panel-slot)) {
            min-height: 34rem;
        }

        #TeamManagerModal :global(.panel-slot) {
            position: absolute;
            top: 0;
            right: 0;
            z-index: 3;
            width: min(22rem, 100%);
            min-height: 100%;
            display: flex;
            box-shadow: var(--bs-box-shadow-lg);
            border-radius: 0.5rem;
        }

        #TeamManagerModal :global(.panel-slot > *) {
            flex: 1 1 auto;
            min-width: 0;
        }
    }

    #TeamManagerModal :global(.soft-badge) {
        color: var(--bs-emphasis-color);
        background-color: var(--bs-secondary-bg);
        border: 1px solid var(--bs-border-color);
        font-weight: 600;
    }

    #TeamManagerModal :global(.nav-link.active .soft-badge) {
        color: var(--bs-primary-text-emphasis);
        background-color: var(--bs-primary-bg-subtle);
        border-color: var(--bs-primary-border-subtle);
    }

    #TeamManagerModal :global(.team-table-wrap) {
        border: 1px solid var(--bs-border-color);
        border-radius: 0.5rem;
        overflow: auto;
        max-height: 55vh;
    }

    #TeamManagerModal :global(.team-table thead th) {
        position: sticky;
        top: 0;
        z-index: 1;
        background: var(--bs-tertiary-bg);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: var(--bs-secondary-color);
        white-space: nowrap;
    }

    #TeamManagerModal :global(.team-table tbody tr[tabindex]) {
        cursor: pointer;
    }

    #TeamManagerModal :global(.team-table tbody tr[tabindex]:focus-visible) {
        outline: 2px solid var(--bs-primary);
        outline-offset: -2px;
    }

    #TeamManagerModal :global(.team-empty) {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 3rem 1rem;
    }

    #TeamManagerModal :global(.team-empty > .bi) {
        font-size: 2.25rem;
        color: var(--bs-secondary-color);
        margin-bottom: 0.5rem;
    }

    .team-empty--hero {
        min-height: 50vh;
    }

    #TeamManagerModal :global(.team-avatar) {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 2rem;
        height: 2rem;
        border-radius: 50%;
        background: var(--bs-primary-bg-subtle);
        color: var(--bs-primary-text-emphasis);
        font-size: 0.75rem;
        font-weight: 700;
        border: 2px solid var(--bs-body-bg);
    }

    #TeamManagerModal :global(.team-avatar--sm) {
        width: 1.6rem;
        height: 1.6rem;
        font-size: 0.65rem;
    }

    #TeamManagerModal :global(.team-avatar--lg) {
        width: 2.5rem;
        height: 2.5rem;
        font-size: 0.9rem;
    }
</style>
